import { createContext, useEffect, useRef, useState } from 'react';

import { KUDU_BUILD_ID } from '@/api/client';

import {
  askBuild,
  KUDU_BUILD_ANSWER,
  KUDU_BUILD_QUERY,
  KUDU_TAKE_OVER_IF_CURRENT,
} from './takeOver';

/**
 * Keeps an open page in step with the build the server serves (issue #66).
 *
 * The page registers the app's worker and looks at the server: as soon as it
 * has registered, every hour while it is shown, and whenever it is shown
 * again. It then compares its own build with the server's, which is the
 * newest worker once its install has settled. When they differ, a new build
 * is ready, and LoadNewVersionOnPageChange loads it when the user next goes
 * to another page. When they match and that worker is waiting, the page asks
 * it to take over, which it does once every open page runs its build.
 *
 * A worker of another build is not always newer than the page. A page outside
 * /app comes from the server while an older build's worker is in charge of the
 * pages under /app, and an older worker can still be waiting when a newer
 * page opens. So only a worker the server is known to serve counts: one seen
 * installing while the page is open, or the newest one right after a look at
 * the server succeeded.
 *
 * Nothing here reloads the page, and nothing happens when the worker in charge
 * of the page changes: the builds before #66 reloaded then, unsaved work and
 * all.
 */

/** The build a new version is ready in, or null: for LoadNewVersionOnPageChange. */
export const AppUpdateContext = createContext<string | null>(null);

const CHECK_EVERY_MS = 60 * 60 * 1000;
// How long a worker has to say which build it runs.
const ASK_TIMEOUT_MS = 1_000;

export interface AppUpdatesOptions {
  /** Asks a worker which build it runs: askBuild, unless a test says otherwise. */
  ask?: (worker: ServiceWorker) => Promise<string | null>;
  /** This page's build. */
  build?: string;
  checkEveryMs?: number;
  /** The browser's, where it has service workers. */
  serviceWorker?: ServiceWorkerContainer;
}

function browserServiceWorker() {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
    ? navigator.serviceWorker
    : undefined;
}

/** Resolves once the worker's install has succeeded or failed. */
function installSettled(worker: ServiceWorker) {
  const settled = () =>
    worker.state === 'installed' || worker.state === 'activated' || worker.state === 'redundant';

  return new Promise<void>((resolve) => {
    if (settled()) {
      resolve();
      return;
    }
    function onStateChange() {
      if (!settled()) return;
      worker.removeEventListener('statechange', onStateChange);
      resolve();
    }
    worker.addEventListener('statechange', onStateChange);
  });
}

export function useAppUpdates(options: AppUpdatesOptions = {}) {
  const [newBuild, setNewBuild] = useState<string | null>(null);
  const firstOptions = useRef(options);

  useEffect(() => {
    const given = firstOptions.current;
    const serviceWorker = 'serviceWorker' in given ? given.serviceWorker : browserServiceWorker();
    if (!serviceWorker) return undefined;

    const container = serviceWorker;
    const build = given.build ?? KUDU_BUILD_ID;
    const ask = given.ask ?? ((worker: ServiceWorker) => askBuild(worker, ASK_TIMEOUT_MS));
    // The workers the server is known to serve, or to have served while the
    // page was open.
    const fromServer = new WeakSet<ServiceWorker>();
    let registration: ServiceWorkerRegistration | undefined;
    let stopped = false;

    function answerBuildQuery(event: MessageEvent<{ type?: unknown } | null>) {
      if (event.data?.type !== KUDU_BUILD_QUERY) return;
      event.ports[0]?.postMessage({ build, type: KUDU_BUILD_ANSWER });
    }

    async function compareWithServer() {
      const current = registration;
      if (!current) return;
      if (current.installing) await installSettled(current.installing);
      if (stopped) return;

      const newest = current.waiting ?? current.active;
      if (!newest || !fromServer.has(newest)) return;
      const newestBuild = await ask(newest);
      if (stopped || newestBuild === null) return;

      if (newestBuild !== build) {
        setNewBuild(newestBuild);
        return;
      }
      setNewBuild(null);
      if (newest === current.waiting) {
        newest.postMessage({ type: KUDU_TAKE_OVER_IF_CURRENT });
      }
    }

    function watchInstall(worker: ServiceWorker | null) {
      if (!worker) return;
      fromServer.add(worker);
      void compareWithServer();
    }

    function watchNewWorker() {
      watchInstall(registration?.installing ?? null);
    }

    async function look() {
      const current = registration;
      if (!current) return;
      try {
        await current.update();
      } catch {
        // Offline, or the server did not answer: nothing learned. The next
        // look tries again.
        return;
      }
      const fresh = current.installing ?? current.waiting ?? current.active;
      if (fresh) fromServer.add(fresh);
      if (!stopped) await compareWithServer();
    }

    function lookWhenShown() {
      if (document.visibilityState === 'visible') void look();
    }

    async function register() {
      try {
        registration = await container.register('/sw.js', { scope: '/app' });
      } catch (error) {
        console.error('Failed to register Kudu service worker', error);
        return;
      }
      // A browser gives a registration or refuses; Playwright, where it blocks
      // workers, gives nothing, and there is then nothing to watch (#113).
      if (stopped || !registration) return;
      registration.addEventListener('updatefound', watchNewWorker);
      watchInstall(registration.installing);
      await look();
    }

    container.addEventListener('message', answerBuildQuery);
    document.addEventListener('visibilitychange', lookWhenShown);
    const hourly = setInterval(lookWhenShown, given.checkEveryMs ?? CHECK_EVERY_MS);
    // Registered once the page has loaded, so its download does not compete
    // with the page's own.
    if (document.readyState === 'complete') {
      void register();
    } else {
      window.addEventListener('load', register, { once: true });
    }

    return () => {
      stopped = true;
      container.removeEventListener('message', answerBuildQuery);
      document.removeEventListener('visibilitychange', lookWhenShown);
      window.removeEventListener('load', register);
      clearInterval(hourly);
      registration?.removeEventListener('updatefound', watchNewWorker);
    };
  }, []);

  // Says so on the page too, for support and for the browser tests: nothing
  // is shown to the user.
  useEffect(() => {
    if (newBuild === null) return undefined;
    document.documentElement.dataset.kuduUpdate = 'ready';
    return () => {
      delete document.documentElement.dataset.kuduUpdate;
    };
  }, [newBuild]);

  return { newBuild };
}
